import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductDrum from '@/pages/home/components/ProductDrum';
import { allProducts } from '@/mocks/products';

const FEATURED_IDS = [1, 2, 3, 4, 5, 6, 7, 8];
const products = allProducts.filter((p) => FEATURED_IDS.includes(p.id));

const categories = ['Todos', 'Tratamiento de Crudo', 'Inhibidores y Protectores', 'Control de Fluidos', 'Tratamiento de Agua'];

export default function FeaturedProducts() {
  const [visible, setVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const filtered = activeCategory === 'Todos'
    ? products
    : products.filter((p) => p.category === activeCategory);

  return (
    <section id="productos" ref={ref} className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary-500 text-sm font-600 tracking-widest uppercase mb-3 block">
            Portafolio de Productos
          </span>
          <h2 className="font-heading font-700 text-4xl md:text-5xl text-foreground-950 leading-tight mb-4">
            Productos <span className="gradient-text">Químicos</span>
          </h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-foreground-600 text-base max-w-2xl mx-auto">
            Línea especializada de aditivos y tratamientos químicos para la industria petrolera, 
            diseñados para optimizar cada fase de perforación, producción y procesamiento.
          </p>
        </div>

        {/* Category filter */}
        <div className={`flex flex-wrap justify-center gap-2 mb-10 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-500 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:text-foreground-900 border border-background-300 hover:border-primary-300'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Products grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((product, i) => (
            <div
              key={product.id}
              onMouseEnter={() => setHoveredId(product.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`relative rounded-xl overflow-hidden bg-white border cursor-pointer card-hover transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${hoveredId === product.id ? 'border-primary-400' : 'border-background-200'}`}
              style={{ transitionDelay: `${i * 0.08 + 0.2}s` }}
            >
              {/* Badge */}
              {product.badge && (
                <div className={`absolute top-3 left-3 z-10 px-3 py-1 rounded-full text-white text-xs font-600 ${product.badgeColor}`}>
                  {product.badge}
                </div>
              )}

              {/* Drum visual */}
              <div className="relative h-52 overflow-hidden">
                <ProductDrum name={product.name} category={product.category} />
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-primary-500 text-xs font-600 tracking-wide uppercase block mb-1">
                      {product.category}
                    </span>
                    <h3 className="font-heading font-700 text-xl text-foreground-950">{product.name}</h3>
                    <p className="text-foreground-500 text-xs mt-0.5">{product.fullName}</p>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                    <i className="ri-flask-line text-primary-400 text-lg"></i>
                  </div>
                </div>

                <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                  {product.description}
                </p>

                {/* Specs */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {product.specs.slice(0, 4).map((spec) => (
                    <div key={spec} className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></div>
                      <span className="text-foreground-600 text-xs">{spec}</span>
                    </div>
                  ))}
                </div>

                {/* Stock availability — two presentations */}
                <div className="flex flex-col gap-1.5 mb-4 px-3 py-2.5 rounded-lg bg-background-100/80">
                  {/* Drum row */}
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const lvl = product.stockDrum >= 100 ? 'high' : product.stockDrum >= 30 ? 'medium' : 'low';
                      const dotColors = { high: 'bg-emerald-500', medium: 'bg-amber-500', low: 'bg-red-500' };
                      return <div className={`w-2 h-2 rounded-full ${dotColors[lvl]} flex-shrink-0`}></div>;
                    })()}
                    <span className="text-foreground-600 text-xs">
                      <span className="font-600 text-foreground-800">{product.stockDrum}</span> Tambores 208L
                    </span>
                  </div>
                  {/* Tote row */}
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const lvl = product.stockTote >= 25 ? 'high' : product.stockTote >= 8 ? 'medium' : 'low';
                      const dotColors = { high: 'bg-emerald-500', medium: 'bg-amber-500', low: 'bg-red-500' };
                      return <div className={`w-2 h-2 rounded-full ${dotColors[lvl]} flex-shrink-0`}></div>;
                    })()}
                    <span className="text-foreground-600 text-xs">
                      <span className="font-600 text-foreground-800">{product.stockTote}</span> Totem 1000L
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/contacto')}
                  className="w-full py-2.5 rounded-md border border-primary-500/40 text-primary-400 text-sm font-600 hover:bg-primary-500 hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Solicitar Cotización
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}